import { useState } from "react";
import { format, eachDayOfInterval, startOfDay, endOfDay, differenceInMinutes } from "date-fns";
import {
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  closestCenter,
  useDroppable,
  useDraggable,
} from "@dnd-kit/core";
import { CSS } from "@dnd-kit/utilities";
import type { ProgramItem } from "../pages/TripDetail";

const categoryColors: Record<string, string> = {
  Reisen: "bg-sky-500",
  Übernachtung: "bg-amber-500",
  Verpflegung: "bg-emerald-500",
  Aktivitäten: "bg-orange-500",
  Workshops: "bg-purple-500",
  Sonstiges: "bg-slate-500",
};

const categoryDotColors: Record<string, string> = {
  Reisen: "bg-sky-400",
  Übernachtung: "bg-amber-400",
  Verpflegung: "bg-emerald-400",
  Aktivitäten: "bg-orange-400",
  Workshops: "bg-purple-400",
  Sonstiges: "bg-slate-400",
};

function getGoogleMapsUrl(item: ProgramItem): string | null {
  // Ortsname als Query priorisieren → Google Maps zeigt Orts-Detailseite statt generischen Pin
  if (item.location) {
    return `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(item.location)}`;
  }
  if (item.location_lat && item.location_lng) {
    return `https://www.google.com/maps/search/?api=1&query=${item.location_lat},${item.location_lng}`;
  }
  return null;
}

/** Groups time-sorted items (with start_time) into overlap clusters for side-by-side rendering */
function groupOverlapping(sortedItems: ProgramItem[]): ProgramItem[][] {
  const timed = sortedItems.filter((i) => !!i.start_time);
  if (timed.length === 0) return [];
  const groups: ProgramItem[][] = [];
  let currentGroup: ProgramItem[] = [timed[0]];
  // Items without end_time are treated as point-in-time (no duration)
  let groupMaxEnd = timed[0].end_time
    ? new Date(timed[0].end_time).getTime()
    : new Date(timed[0].start_time!).getTime();

  for (let i = 1; i < timed.length; i++) {
    const item = timed[i];
    const itemStart = new Date(item.start_time!).getTime();
    const itemEnd = item.end_time ? new Date(item.end_time).getTime() : itemStart;

    if (itemStart < groupMaxEnd) {
      currentGroup.push(item);
      groupMaxEnd = Math.max(groupMaxEnd, itemEnd);
    } else {
      groups.push(currentGroup);
      currentGroup = [item];
      groupMaxEnd = itemEnd;
    }
  }
  groups.push(currentGroup);
  return groups;
}

function ItemCard({
  item,
  isOrganizer,
  onEditItem,
  variant = "scheduled",
  ghost = false,
}: {
  item: ProgramItem;
  isOrganizer: boolean;
  onEditItem: (item: ProgramItem) => void;
  variant?: "scheduled" | "dateOnly" | "floating";
  ghost?: boolean;
}) {
  const badgeColor = categoryColors[item.category] ?? "bg-slate-500";
  const mapsUrl = getGoogleMapsUrl(item);

  let timeLabel: string;
  if (variant === "floating") {
    timeLabel = "Termin offen";
  } else if (variant === "dateOnly") {
    timeLabel = "ganztägig";
  } else {
    const start = new Date(item.start_time!);
    if (item.end_time) {
      const end = new Date(item.end_time);
      const duration = differenceInMinutes(end, start);
      const hours = Math.floor(duration / 60);
      const mins = duration % 60;
      const durationStr =
        hours > 0 ? (mins > 0 ? `${hours}h ${mins}min` : `${hours}h`) : `${mins}min`;
      timeLabel = `${format(start, "HH:mm")} – ${format(end, "HH:mm")} · ${durationStr}`;
    } else {
      timeLabel = format(start, "HH:mm");
    }
  }

  return (
    <div className="bg-[#161b27] rounded-xl border border-[#2a3347] px-4 py-3 hover:border-sky-500/30 transition-colors">
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1.5 flex-wrap">
            <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full text-white ${badgeColor}`}>
              {item.category}
            </span>
            <span className={`text-[10px] ${variant === "scheduled" ? "text-slate-500" : "text-slate-600 italic"}`}>
              {timeLabel}
            </span>
          </div>
          <h4 className="text-sm font-semibold text-slate-100">{item.title}</h4>
          {item.description && (
            <p className="text-xs text-slate-400 mt-1 leading-relaxed">{item.description}</p>
          )}
          {!ghost && mapsUrl && (
            <a
              href={mapsUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[10px] text-sky-400 hover:text-sky-300 mt-1.5 block truncate transition-colors"
              onClick={(e) => e.stopPropagation()}
            >
              📍 {item.location || "Ort auf Karte"}
            </a>
          )}
        </div>
        {!ghost && isOrganizer && (
          <button
            onClick={() => onEditItem(item)}
            className="text-slate-600 hover:text-slate-300 transition-colors shrink-0 p-1 text-sm"
            title="Bearbeiten"
          >
            ⚙️
          </button>
        )}
      </div>
    </div>
  );
}

/** Wrapper der ItemCard draggable macht und einen Grip-Handle hinzufügt */
function DraggableItemWrapper({
  item,
  isOrganizer,
  onEditItem,
  canDrag,
  variant,
}: {
  item: ProgramItem;
  isOrganizer: boolean;
  onEditItem: (item: ProgramItem) => void;
  canDrag: boolean;
  variant?: "scheduled" | "dateOnly" | "floating";
}) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
    id: item.id,
    disabled: !canDrag,
  });

  const style = transform
    ? { transform: CSS.Translate.toString(transform), opacity: isDragging ? 0.35 : 1 }
    : undefined;

  return (
    <div ref={setNodeRef} style={style} className="flex items-stretch gap-1">
      {canDrag && (
        <div
          {...listeners}
          {...attributes}
          className="cursor-grab active:cursor-grabbing text-slate-700 hover:text-slate-500 flex items-center px-0.5 touch-none shrink-0 transition-colors"
          title="Auf anderen Tag verschieben"
        >
          ⠿
        </div>
      )}
      <div className="flex-1 min-w-0">
        <ItemCard
          item={item}
          isOrganizer={isOrganizer}
          onEditItem={onEditItem}
          variant={variant}
        />
      </div>
    </div>
  );
}

/** Droppable Tages-Container */
function DroppableDay({ id, children }: { id: string; children: React.ReactNode }) {
  const { setNodeRef, isOver } = useDroppable({ id });
  return (
    <div
      ref={setNodeRef}
      className={`rounded-xl transition-colors duration-150 ${
        isOver ? "ring-1 ring-sky-500/40 bg-sky-500/5" : ""
      }`}
    >
      {children}
    </div>
  );
}

interface Props {
  items: ProgramItem[];
  trip: { start_date: string; end_date: string };
  isOrganizer: boolean;
  onEditItem: (item: ProgramItem) => void;
  onMoveItem?: (itemId: string, newDate: string) => void;
  expandedDays: Record<string, boolean>;
  setExpandedDays: React.Dispatch<React.SetStateAction<Record<string, boolean>>>;
}

export default function VerticalTimeline({
  items,
  trip,
  isOrganizer,
  onEditItem,
  onMoveItem,
  expandedDays,
  setExpandedDays,
}: Props) {
  const [activeId, setActiveId] = useState<string | null>(null);

  const days = eachDayOfInterval({
    start: new Date(trip.start_date),
    end: new Date(trip.end_date),
  });

  const canDrag = isOrganizer && !!onMoveItem;

  // Classify items
  const floatingItems = items.filter((i) => !i.start_time && !i.scheduled_date);

  const toggleAll = (expand: boolean) => {
    const newState: Record<string, boolean> = {};
    days.forEach((d) => {
      newState[d.toISOString()] = expand;
    });
    setExpandedDays(newState);
  };

  const handleDragStart = ({ active }: DragStartEvent) => {
    setActiveId(active.id as string);
  };

  const handleDragEnd = ({ active, over }: DragEndEvent) => {
    setActiveId(null);
    if (!over || !onMoveItem) return;
    const newDate = over.id as string; // DroppableDay-ID = "yyyy-MM-dd"
    const item = items.find((i) => i.id === active.id);
    if (!item) return;
    const currentDate = item.start_time
      ? item.start_time.substring(0, 10)
      : item.scheduled_date ?? null;
    if (currentDate !== newDate) {
      onMoveItem(active.id as string, newDate);
    }
  };

  const activeItem = activeId ? items.find((i) => i.id === activeId) : null;
  const activeVariant = activeItem?.start_time
    ? "scheduled"
    : activeItem?.scheduled_date
    ? "dateOnly"
    : "floating";

  return (
    <DndContext
      onDragStart={handleDragStart}
      onDragEnd={handleDragEnd}
      collisionDetection={closestCenter}
    >
      <div className="mt-4 px-4 sm:px-0">
        <div className="flex justify-between items-center mb-5">
          <h3 className="text-xs uppercase tracking-widest text-slate-500 font-medium">
            Programm
          </h3>
          <div className="space-x-3 text-xs">
            <button
              onClick={() => toggleAll(true)}
              className="text-sky-400 hover:text-sky-300 transition-colors"
            >
              Alle öffnen
            </button>
            <button
              onClick={() => toggleAll(false)}
              className="text-sky-400 hover:text-sky-300 transition-colors"
            >
              Alle schließen
            </button>
          </div>
        </div>

        {/* Ungeplante Items (kein Datum, keine Zeit) */}
        {floatingItems.length > 0 && (
          <div className="mb-6">
            <p className="text-[10px] uppercase tracking-widest text-slate-600 font-medium mb-3 px-1">
              Noch nicht terminiert
            </p>
            <div className="space-y-2 pl-4">
              {floatingItems.map((item) => (
                <DraggableItemWrapper
                  key={item.id}
                  item={item}
                  isOrganizer={isOrganizer}
                  onEditItem={onEditItem}
                  canDrag={canDrag}
                  variant="floating"
                />
              ))}
            </div>
          </div>
        )}

        {/* Continuous timeline container */}
        <div className="relative">
          {/* Vertical line — always visible, spans all days */}
          <div className="absolute left-[3.5rem] top-0 bottom-0 w-px bg-[#2a3347]" />

          {days.map((day) => {
            const dayKey = day.toISOString();
            const dayDateStr = format(day, "yyyy-MM-dd");
            // Default: expanded
            const isExpanded = expandedDays[dayKey] ?? true;
            const dayStart = startOfDay(day);
            const dayEnd = endOfDay(day);

            // Items with full time for this day
            const scheduledDayItems = items
              .filter((item) => {
                if (!item.start_time) return false;
                const start = new Date(item.start_time);
                return start >= dayStart && start <= dayEnd;
              })
              .sort(
                (a, b) =>
                  new Date(a.start_time!).getTime() - new Date(b.start_time!).getTime()
              );

            // Items with only a date for this day
            const dateOnlyDayItems = items.filter((item) => {
              if (item.start_time || !item.scheduled_date) return false;
              return item.scheduled_date === dayDateStr;
            });

            const allDayItems = [...scheduledDayItems, ...dateOnlyDayItems];

            const groups = groupOverlapping(scheduledDayItems);

            return (
              <DroppableDay key={dayKey} id={dayDateStr}>
                {/* Day header */}
                <button
                  onClick={() =>
                    setExpandedDays((prev) => ({ ...prev, [dayKey]: !isExpanded }))
                  }
                  className="relative w-full text-left py-5 px-1 hover:bg-white/[0.03] rounded-xl transition-colors flex items-center"
                >
                  {/* Milestone circle ON the line */}
                  <div className="absolute left-[3.5rem] -translate-x-1/2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-slate-700 flex flex-col items-center justify-center z-10 shrink-0">
                    <span className="text-[9px] font-bold text-slate-100 leading-none uppercase tracking-wide">
                      {format(day, "EEE")}
                    </span>
                    <span className="text-[8px] text-slate-400 leading-none mt-0.5">
                      {format(day, "dd.MM.")}
                    </span>
                  </div>

                  {/* Spacer to clear the circle */}
                  <div className="w-20 shrink-0" />

                  {/* Summary dots */}
                  {allDayItems.length > 0 && (
                    <>
                      <div className="flex gap-1 shrink-0">
                        {allDayItems.slice(0, 6).map((item) => (
                          <span
                            key={item.id}
                            className={`w-1.5 h-1.5 rounded-full ${categoryDotColors[item.category] ?? "bg-slate-400"}`}
                          />
                        ))}
                      </div>
                      {!isExpanded && (
                        <span className="text-xs text-slate-500 truncate ml-1">
                          {allDayItems[0].title}
                        </span>
                      )}
                    </>
                  )}

                  {allDayItems.length === 0 && !isExpanded && (
                    <span className="text-xs text-slate-700 italic">Nichts geplant</span>
                  )}
                </button>

                {/* Expanded content */}
                {isExpanded && (
                  <div className="pt-1 pb-6">
                    {allDayItems.length === 0 ? (
                      <p className="pl-16 text-slate-700 italic text-sm py-3">
                        💤 Für diesen Tag ist nichts geplant.
                      </p>
                    ) : (
                      <div className="relative pl-16">
                        {/* Datum-only Items zuoberst */}
                        {dateOnlyDayItems.length > 0 && (
                          <div className="space-y-2 mb-4">
                            {dateOnlyDayItems.map((item) => (
                              <DraggableItemWrapper
                                key={item.id}
                                item={item}
                                isOrganizer={isOrganizer}
                                onEditItem={onEditItem}
                                canDrag={canDrag}
                                variant="dateOnly"
                              />
                            ))}
                          </div>
                        )}

                        <div className="space-y-5">
                          {groups.map((group, groupIdx) => {
                            const groupStartTime = Math.min(
                              ...group.map((item) => new Date(item.start_time!).getTime())
                            );
                            const groupStart = new Date(groupStartTime);
                            const firstDotColor =
                              categoryDotColors[group[0].category] ?? "bg-slate-400";

                            return (
                              <div key={groupIdx} className="relative">
                                {/* Time label */}
                                <div className="absolute -left-16 top-3 text-xs text-slate-500 text-right w-12 leading-none">
                                  {format(groupStart, "HH:mm")}
                                </div>

                                {/* Dot on the line */}
                                <div
                                  className={`absolute -left-[0.9rem] top-3.5 w-3 h-3 rounded-full ${firstDotColor} ring-2 ring-[#0d1117] z-10`}
                                />

                                {group.length === 1 ? (
                                  <DraggableItemWrapper
                                    item={group[0]}
                                    isOrganizer={isOrganizer}
                                    onEditItem={onEditItem}
                                    canDrag={canDrag}
                                  />
                                ) : (
                                  <div className="flex gap-2">
                                    {group.map((item) => (
                                      <div key={item.id} className="flex-1 min-w-0">
                                        <DraggableItemWrapper
                                          item={item}
                                          isOrganizer={isOrganizer}
                                          onEditItem={onEditItem}
                                          canDrag={canDrag}
                                        />
                                      </div>
                                    ))}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </DroppableDay>
            );
          })}
        </div>
      </div>

      {/* Ghost-Card beim Drag */}
      <DragOverlay dropAnimation={null}>
        {activeItem && (
          <div className="opacity-80 shadow-2xl rotate-1 scale-105">
            <ItemCard
              item={activeItem}
              isOrganizer={false}
              onEditItem={() => {}}
              variant={activeVariant as "scheduled" | "dateOnly" | "floating"}
              ghost
            />
          </div>
        )}
      </DragOverlay>
    </DndContext>
  );
}
